import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { SectionMetadata, SectionMetadataType } from 'app/providers/storage-api/collection.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

interface SectionForm {
  isOn: FormControl<boolean>;
  rows: FormArray<FormGroup<RowForm>>;
}

interface RowForm {
  field: FormControl<string>;
  buckets: FormControl<string[]>;
}

export interface SelectedSection {
  field: string;
  buckets: string[];
  type?: SectionMetadataType;
  negated?: boolean;
}

const NOT_PRESENT = 'not_present';

@EnableClassTelemetry()
@Component({
  selector: 'app-section-selector',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './section-selector.component.html',
  styleUrls: ['./section-selector.component.scss'],
})
export class SectionSelectorComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() sectionMetadata!: SectionMetadata[];
  @Input() forFilters?: boolean;
  @Input() compact?: boolean;
  @Input() isToggled?: boolean;
  @Input() disabled?: boolean;
  @Input() value?: SelectedSection[];

  @Output() sectionSelect = new EventEmitter<SelectedSection[] | undefined>();
  @Output() onToggle = new EventEmitter<boolean>();

  form: FormGroup<SectionForm>;

  fieldDs: DropdownObject[];
  availableFields: DropdownObject[];

  private readonly destroy$ = new Subject<void>();
  private subs = new Subscription();
  bucketMap = new Map<string, { id: string; title: string }[]>();

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.buildForm();
    this.buildFieldDs(false);
    this.fillForm();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.unsubscribe();
  }

  private buildForm() {
    this.form = this.formBuilder.group<SectionForm>({
      isOn: new FormControl({ value: !!this.isToggled, disabled: !!this.disabled }),
      rows: this.formBuilder.array([
        this.formBuilder.group<RowForm>({
          field: new FormControl({ value: null, disabled: !!this.disabled }),
          buckets: new FormControl({ value: [], disabled: !!this.disabled }),
        }),
      ]),
    });

    this.subs.add(
      this.form.controls.isOn.valueChanges.subscribe((isOn) => {
        this.onToggle.emit(isOn);
        if (!isOn && !this.sectionMetadata.length) {
          this.form.controls.isOn.disable({ emitEvent: false });
        }

        if (isOn) {
          const fieldIdToPatch = this.fieldDs.find((f) =>
            this.form.controls.rows.value.some((r) => r.field === f.id)
          )?.id;
          // Patch only the first item
          this.form.controls.rows.at(0).patchValue({ field: fieldIdToPatch, buckets: [] });
        } else {
          // TODO maybe reset and remove first item
          this.bucketMap.clear();
          // Keep only the first item
          for (let i = this.form.controls.rows.length - 1; i > 0; i--) {
            this.onRemoveRow(i);
          }
        }
      })
    );
    this.subs.add(
      this.form.controls.rows.valueChanges.subscribe((rows) => {
        this.bucketMap.clear();
        rows.filter((row) => row.field).forEach((row) => this.handleFieldChange(row));
      })
    );

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (!value.isOn) {
        this.sectionSelect.emit(undefined);
        return;
      }
      // The field reassign may not be required but it comes back as Partial from the value
      const sectionsSelected = value.rows.map((row) => {
        // Keep only the available buckets in the row
        const availableBucketValues = row.field ? this.getAvailableBuckets(row.field) : [];
        const buckets = row.buckets?.filter((b) => availableBucketValues.includes(b)) ?? [];
        return {
          field: row.field,
          buckets: buckets,
          type: this.sectionMetadata.find((sm) => sm.fieldName === row.field)?.type,
        };
      });
      this.sectionSelect.emit(sectionsSelected);
    });
  }

  private handleFieldChange(value) {
    if (!value.field) {
      return;
    }
    const bucketMapperFunction = (v: string): { id: string; title: string } => ({
      id: v,
      title: v === NOT_PRESENT ? translate('widgets.section-selector.not-present') : v,
    });
    let availableBucketValues = this.getAvailableBuckets(value.field).map(bucketMapperFunction);

    const selectedValue: SelectedSection | undefined = this.value?.find(
      (section: SelectedSection) => section.field === value.field
    );
    if (selectedValue?.buckets) {
      const existingBucketIds: Set<string> = new Set(
        availableBucketValues.map((bucket) => bucket.id)
      );
      selectedValue.buckets.forEach((bucket: string) => {
        if (!existingBucketIds.has(bucket)) {
          availableBucketValues = [...availableBucketValues, bucketMapperFunction(bucket)];
          existingBucketIds.add(bucket);
        }
      });
    }

    if (!this.bucketMap.has(value.field)) {
      this.bucketMap.set(value.field, []);
    }
    this.bucketMap.set(value.field, availableBucketValues);

    this.fieldDs = this.fieldDs.map((f) => ({ ...f, disabled: this.bucketMap.has(f.id) }));
  }

  onRemoveRow(index) {
    const control = this.form.controls.rows.at(index);
    this.bucketMap.has(control.value.field) && this.bucketMap.delete(control.value.field);
    this.fieldDs = this.fieldDs.map((f) => ({ ...f, disabled: this.bucketMap.has(f.id) }));
    this.form.controls.rows.removeAt(index);
  }

  onAddRow(fieldDefaultValue?: string, bucketsDefaultValue?: string[]) {
    const freeValue = this.getAvailableFieldId();
    const rowGroup = this.formBuilder.group<RowForm>({
      field: new FormControl({
        value: fieldDefaultValue || freeValue || null,
        disabled: !!this.disabled,
      }),
      buckets: new FormControl({ value: bucketsDefaultValue || [], disabled: !!this.disabled }),
    });
    this.form.controls.rows.push(rowGroup, { emitEvent: true });
  }

  private getAvailableFieldId() {
    return this.fieldDs.find((f) => this.isFieldAvailable(f.id))?.id;
  }

  private isFieldAvailable(id: string) {
    return id && !this.reservedFieldIds.includes(id);
  }

  private getAvailableBuckets(field: string): string[] {
    const sectionMetadataBucketValues = this.sectionMetadata
      .find((i) => i.fieldName === field)
      ?.items.map((b) => b.value);

    const selectedBucketValues: string[] = [];

    const bucketValues = Utils.mergeArrays(sectionMetadataBucketValues, selectedBucketValues).sort(
      (a, b) => a.localeCompare(b)
    );
    bucketValues.unshift(NOT_PRESENT);
    return bucketValues;
  }

  private buildFieldDs(isOnlyMetadata = true): void {
    const createDdo = (fieldName: string, type?: SectionMetadataType) => ({
      id: fieldName,
      title:
        type === 'property'
          ? translate(`widgets.section-selector.${fieldName}`)
          : FieldService.getFieldLongName(fieldName),
    });

    const fields = this.sectionMetadata.map((s) => createDdo(s.fieldName, s.type));

    if (
      !isOnlyMetadata &&
      this.value &&
      !fields.find((f) => this.value.some((sec) => sec.field === f.id))
    ) {
      this.value.forEach((section) => {
        fields.push(createDdo(section.field, section.type));
      });
    }

    this.fieldDs = fields;
  }

  private fillForm(): void {
    if (!this.sectionMetadata.length && !this.value) {
      this.form.controls.isOn.disable({ emitEvent: false });
      return;
    }

    if (!this.value) {
      this.form.controls.rows.at(0).patchValue({ field: this.fieldDs[0].id });
      return;
    }

    this.form.patchValue({
      isOn: true,
    });

    if (this.value?.length) {
      this.form.controls.rows.removeAt(0);
      this.value.forEach((section) => {
        if (this.isFieldAvailable(section.field)) {
          this.onAddRow(section.field, section.buckets);
        }
      });
    }
  }

  get isOn(): boolean {
    return this.form.controls.isOn.value;
  }

  get formRows() {
    return this.form.controls.rows;
  }

  get reservedFieldIds() {
    return this.form.controls.rows.value.map((r) => r.field).filter(Boolean);
  }
}
